import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { Store, User as UserIcon } from "lucide-react";

const Auth = () => {
  const { t } = useApp();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState("buyer");
  const [isPro, setIsPro] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", shop_name: "", shop_desc: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        const payload = { name: form.name, email: form.email, password: form.password, role };
        if (role === "seller") {
          payload.shop_name = form.shop_name;
          payload.shop_desc = form.shop_desc;
        }
        if (role === "buyer") payload.is_pro = isPro;
        u = await register(payload);
      }
      if (u.role === "admin") navigate("/admin");
      else if (u.role === "seller") navigate("/seller");
      else navigate("/account");
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 bg-white border border-border rounded-sm text-sm outline-none focus:border-[#b08d57] transition-colors";

  return (
    <div className="max-w-md mx-auto px-4 py-16 fade-up">
      <div className="text-center mb-8">
        <div className="h-px w-10 bg-[#b08d57] mx-auto mb-4" />
        <h1 className="font-serif-lux text-4xl">{mode === "login" ? t("signIn") : t("signUp")}</h1>
      </div>

      {mode === "register" && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button type="button" data-testid="role-buyer" onClick={() => setRole("buyer")}
            className={`flex flex-col items-center gap-2 py-4 border rounded-sm text-sm transition-all ${role === "buyer" ? "border-[#1b1b1b] bg-[#1b1b1b] text-white" : "border-border hover:border-[#1b1b1b]"}`}>
            <UserIcon className="h-5 w-5" strokeWidth={1.5} /> {t("accountBuyer")}
          </button>
          <button type="button" data-testid="role-seller" onClick={() => setRole("seller")}
            className={`flex flex-col items-center gap-2 py-4 border rounded-sm text-sm transition-all ${role === "seller" ? "border-[#1b1b1b] bg-[#1b1b1b] text-white" : "border-border hover:border-[#1b1b1b]"}`}>
            <Store className="h-5 w-5" strokeWidth={1.5} /> {t("accountSeller")}
          </button>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && (
          <input data-testid="auth-name" className={inputCls} placeholder={t("fullName")} value={form.name} onChange={set("name")} required />
        )}
        <input data-testid="auth-email" className={inputCls} type="email" placeholder={t("emailLabel")} value={form.email} onChange={set("email")} required />
        <input data-testid="auth-password" className={inputCls} type="password" placeholder={t("passwordLabel")} value={form.password} onChange={set("password")} required />
        {mode === "register" && role === "seller" && (
          <>
            <input data-testid="auth-shop-name" className={inputCls} placeholder={t("shopNameLabel")} value={form.shop_name} onChange={set("shop_name")} required />
            <textarea data-testid="auth-shop-desc" className={inputCls} rows={3} placeholder={t("shopDescLabel")} value={form.shop_desc} onChange={set("shop_desc")} />
          </>
        )}
        {mode === "register" && role === "buyer" && (
          <label data-testid="auth-pro-toggle" className="flex items-start gap-3 border border-border rounded-sm p-3 cursor-pointer hover:border-[#b08d57] transition-colors">
            <input type="checkbox" checked={isPro} onChange={(e) => setIsPro(e.target.checked)} className="mt-1 accent-[#b08d57]" data-testid="auth-pro-checkbox" />
            <span className="text-sm text-[#1b1b1b]">{t("proAccount")}<span className="block text-xs text-muted-foreground mt-0.5">{t("proAccountHint")}</span></span>
          </label>
        )}
        {error && <p className="text-sm text-[#8b1e3f]">{error}</p>}
        <button type="submit" data-testid="auth-submit" disabled={busy}
          className="w-full bg-[#1b1b1b] text-white py-3.5 text-sm tracking-[0.1em] uppercase hover:bg-[#b08d57] transition-colors disabled:opacity-60">
          {mode === "login" ? t("signIn") : t("signUp")}
        </button>
      </form>

      <div className="text-center mt-6 text-sm text-[#555]">
        {mode === "login" ? (
          <button data-testid="auth-toggle" onClick={() => { setMode("register"); setError(""); }} className="hover:text-[#b08d57]">
            {t("noAccount")} <span className="text-[#b08d57] underline">{t("signUp")}</span>
          </button>
        ) : (
          <button data-testid="auth-toggle" onClick={() => { setMode("login"); setError(""); }} className="hover:text-[#b08d57]">
            {t("haveAccount")} <span className="text-[#b08d57] underline">{t("signIn")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Auth;
